import { writable, derived, get } from 'svelte/store';
import * as apiService from './apiService';
import type { VerifyCodeRequest } from '../types';

// Create a more robust auth store
interface AuthState {
    token: string | null;
    user: any | null;
    loading: boolean;
}

const API_URL = (import.meta as any).env.VITE_API_URL || 'http://localhost:3000';

// Declare auth store before using it
let auth: ReturnType<typeof createAuthStore>;

async function fetchCurrentUser() {
    const state = get(auth);
    if (!state.token) {
        auth.logout();
        return null;
    }

    return new Promise((resolve, reject) => {
        apiService.getCurrentUser((result, status) => {
            if (status === 200) {
                auth.setUser(result);
                resolve(result);
            } else {
                auth.logout();
                reject(new Error(typeof result === 'string' ? result : JSON.stringify(result)));
            }
        });
    });
}

const createAuthStore = () => {
    const initialToken = localStorage.getItem('token');
    
    const { subscribe, set, update } = writable<AuthState>({
        token: initialToken,
        user: null,
        loading: !!initialToken
    });

    const store = {
        subscribe,
        setToken: (token: string | null) => {
            if (token) {
                localStorage.setItem('token', token);
            } else {
                localStorage.removeItem('token');
            }
            update(state => ({ ...state, token }));
        },
        setUser: (user: any) => {
            update(state => ({ ...state, user, loading: false }));
        },
        logout: () => {
            localStorage.removeItem('token');
            set({ token: null, user: null, loading: false });
        },
        fetchUser: async () => {
            const state = get(auth);
            if (!state.token) {
                store.logout();
                return null;
            }

            return new Promise((resolve, reject) => {
                apiService.getCurrentUser((result, status) => {
                    if (status === 200) {
                        store.setUser(result);
                        resolve(result);
                    } else {
                        store.logout();
                        reject(new Error(typeof result === 'string' ? result : JSON.stringify(result)));
                    }
                });
            });
        }
    };

    return store;
};

// Initialize the auth store
auth = createAuthStore();

// If we have an initial token, fetch the user data after store creation
if (localStorage.getItem('token')) {
    fetchCurrentUser().catch(() => auth.logout());
}

export { auth };

// Derived store for loading state
export const isLoading = derived(auth, $auth => $auth.loading);


//TODO: Move to apiService?
export async function requestMagicLink(phoneNumber: string, countryCode: string, returnTo: string) {
    return new Promise((resolve, reject) => {
        apiService.requestMagicLink(phoneNumber, countryCode, returnTo, (result, status) => {
            if (status === 200) {
                resolve(result);
            } else {
                reject(new Error(typeof result === 'string' ? result : JSON.stringify(result)));
            }
        });
    });
}

export async function verifyMagicLink(token: string) {
    return new Promise((resolve, reject) => {
        apiService.verifyMagicLink(token, (result, status) => {
            if (status === 200) {
                // Check if this is a resend response
                if (result.resend) {
                    // Return the result but don't set token since it's empty
                    resolve({ ...result, resend: true });
                } else {
                    // Normal token response
                    localStorage.setItem('token', result.access_token);
                    auth.setToken(result.access_token);
                    // Wait a short time to ensure token is properly set
                    setTimeout(() => resolve(result), 100);
                }
            } else {
                // Provide more specific error messages based on status code
                let errorMessage = 'Invalid or expired token';
                if (status === 400) {
                    errorMessage = typeof result === 'string' ? result : 'Invalid or expired token';
                } else if (status === 401) {
                    errorMessage = 'Unauthorized - token may be invalid';
                } else if (status === 404) {
                    errorMessage = 'Token not found';
                } else if (status >= 500) {
                    errorMessage = 'Server error - please try again later';
                }
                reject(new Error(errorMessage));
            }
        });
    });
}

export async function verifyCode(payload: VerifyCodeRequest) {
    return new Promise((resolve, reject) => {
        apiService.verifyCode(payload, (result, status) => {
            if (status === 200) {
                // Check if this is a resend response
                if (result.resend) {
                    // Return the result but don't set token since it's empty
                    resolve({ ...result, resend: true });
                } else {
                    // Normal token response
                    localStorage.setItem('token', result.access_token);
                    auth.setToken(result.access_token);
                    resolve(result);
                }
            } else {
                reject(new Error(typeof result === 'string' ? result : JSON.stringify(result)));
            }
        });
    });
}

export async function resendMagicLink(token: string) {
    return new Promise((resolve, reject) => {
        apiService.resendMagicLink(token, (result, status) => {
            if (status === 200) {
                resolve(result);
            } else {
                reject(new Error(typeof result === 'string' ? result : JSON.stringify(result)));
            }
        });
    });
}

// Email-based verification
export async function verifyEmailCode(payload: { code: number, email: string, invitation_code?: string }) {
    return new Promise((resolve, reject) => {
        apiService.verifyEmailSignup(payload, (result, status) => {
            if (status === 200) {
                // Normal token response
                localStorage.setItem('token', result.access_token);
                auth.setToken(result.access_token);
                resolve(result);
            } else {
                reject(new Error(typeof result === 'string' ? result : JSON.stringify(result)));
            }
        });
    });
}

